import { A, pipe } from "@mobily/ts-belt"
import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { LessonEditMove } from "@shared/models/analytics/lesson"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import type { Course, CourseCalendarDateCustom } from "@shared/models/course"
import type { Planbook } from "@shared/models/planbook"
import type { RotationCalendar } from "@shared/models/rotation-calendar"
import { findCourseDateCustom } from "cc-frontend/lib/find-course-date"
import type { PatchModel } from "@shared/models/patch"
import type { PreparedAction } from "../../../action-doc-builder"
import { compact, find, flatMap, map, partial } from "lodash-es"
import moment from "moment"
import type { Narrative } from "../../create-narrative"
import defaultLessonModels from "../../lesson/default-lesson-models"
import pullLessonBackwardsAction from "../../lesson/PULL_LESSON_BACKWARDS"

interface Payload {
  dateString: string
  courses: Array<Course>
  planbook: Planbook
  rotationCalendar: RotationCalendar
}

export const name = "PULL_MULTIPLE_LESSONS_BACKWARDS"

export function preparePullMulitpleLessonsBackwards(payload: Payload): PreparedAction {
  return {
    name: name,
    payload: payload,
    patches: patches(payload),
    undoPatches: undoPatches(payload),
    narrative: partial(narrative, payload),
  }
}

export function patches(payload: Payload): Array<PatchModel> {
  return compact(
    flatMap(payload.courses, (course) => {
      let action = pullLessonBackwardsAction({
        dateString: payload.dateString,
        course: course,
        planbook: payload.planbook,
        rotationCalendar: payload.rotationCalendar,
      })
      return action.patches
    })
  )
}

export function undoPatches(payload: Payload): Array<PatchModel> {
  return compact(
    flatMap(payload.courses, (course) => {
      let action = pullLessonBackwardsAction({
        dateString: payload.dateString,
        course: course,
        planbook: payload.planbook,
        rotationCalendar: payload.rotationCalendar,
      })
      return action.undoPatches
    })
  )
}

export function narrative(payload: Payload, findFn: FindFn, userId: string): Narrative | null {
  let planbookId = payload.planbook.id
  let activityToValues = map(payload.courses, (course) => {
    return `course-edits:course-${course.id}`
  })
  let courseDateArray = compact(
    map(payload.courses, (course) => {
      return find(
        course.attributes.calendar.dates,
        (date: CourseCalendarDateCustom) => date.attributes.dateString === payload.dateString
      )
    })
  )
  let cardStackIds = map(courseDateArray, (date) => {
    return date.attributes.cardStackId
  })
  let cardStackArray = compact(
    map(courseDateArray, (date) => {
      if (date.attributes.cardStackId === undefined || date.attributes.cardStackId === null) return
      return (
        findFn("card-stack", date.attributes.cardStackId) ||
        findFn("card-stack-summary", date.attributes.cardStackId)
      )
    })
  )
  let cardStackTitles = map(cardStackArray, (cardStack) => {
    return cardStack.attributes.title
  })

  activityToValues.push(`planbook-edits:planbook-${planbookId}`)
  let courseCount = payload.courses.length
  let formattedDate = moment(payload.dateString).format("ddd, MMM. D, YYYY")
  let analyticsEvents = pipe(
    payload.courses,
    A.filter((course) => {
      let courseDate = findCourseDateCustom(course, payload.dateString)
      return (
        courseDate?.attributes.cardStackId !== undefined &&
        courseDate.attributes.cardStackId !== null
      )
    }),
    A.map((course): LessonEditMove | null => {
      let courseDate = findCourseDateCustom(course, payload.dateString)
      let cardStackId = courseDate?.attributes.cardStackId
      if (cardStackId === undefined || cardStackId === null) return null
      let models = defaultLessonModels(
        { cardStackId: cardStackId, planbookId: payload.planbook.id },
        findFn,
        userId
      )
      return {
        ...createBlankEvent(),
        root_id: payload.planbook.id,
        root_type: "planbook",
        date_plan_scheduled_for: models.dateStringForCardStack,
        primary_object_id: cardStackId,
        primary_object_id_and_ancestor_ids: models.cardStackIdAndAncestorIds,
        primary_verb: "move",
        primary_possessive: getPossessiveForPlanbook(findFn, userId, payload.planbook.id),
        primary_object_type: "lesson",
        primary_object_specifier: null,

        adverbial_verb: "bumping",
        adverbial_object_type: "day",
        adverbial_object_specifier: "backward",
        adverbial_object_id: payload.dateString,
        adverbial_possessive: null,
        adverbial_object_id_and_ancestor_ids: models.cardStackIdAndAncestorIds,
      }
    }),
    (arr) => compact(arr)
  )
  return {
    title: "Pulled Back Lessons",
    titleForAnalytics: "Day - Pull Back",
    description: `Moved ${courseCount} lessons backwards to ${formattedDate}`,
    context: {
      courseIds: map(payload.courses, "id"),
      planbookTitle: payload.planbook.attributes.title,
      planbookId: payload.planbook.id,
      cardStackIds,
      cardStackTitles,
    },
    activity: {
      actor: `user-${userId}`,
      verb: "edited",
      object: `planbook-${planbookId}`,
      to: activityToValues,
    },
    newAnalyticsEvents: analyticsEvents,
  }
}
